import React from 'react';
import { Plus, Trash2, Box } from 'lucide-react';
import { calculateProductTax } from '../utils/taxCalculations';
import { GST_TYPES } from '../utils/constants';

const ProductTable = ({
    products,
    setProducts,
    gstType,
    sellerGST,
    buyerGST,
    isPreview,
    isInterState,
    variant
}) => {
    const isClassic = variant === 'classic';
    const isBillOfSupply = gstType !== 'Regular GST';
    // const isComposite = gstType === GST_TYPES.COMPOSITE; // Unused for now

    const addProduct = () => {
        setProducts([
            ...products,
            {
                id: Date.now(),
                name: '',
                hsnCode: '',
                rate: '',
                quantity: 1,
                gstRate: 18,
            }
        ]);
    };

    const removeProduct = (id) => {
        setProducts(products.filter(p => p.id !== id));
    };

    const updateProduct = (id, field, value) => {
        setProducts(products.map(p =>
            p.id === id ? { ...p, [field]: value } : p
        ));
    };

    // Helper to render currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    return (
        <div className="w-full">

            {/* Header Section - Matches Screenshot */}
            {!isPreview && (
                <div className="flex justify-between items-center p-4 border-b border-slate-200 bg-white">
                    <h3 className="text-lg font-bold text-slate-800 uppercase tracking-tight">Item Details</h3>
                    <button
                        onClick={addProduct}
                        className="flex items-center px-4 py-1.5 text-blue-900 text-sm font-semibold rounded border border-blue-900 hover:bg-blue-50 transition-colors"
                    >
                        <Plus size={16} className="mr-1" />
                        Add Item
                    </button>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className={`w-full text-left border-collapse ${isClassic ? 'border-2 border-slate-800' : ''}`}>
                    <thead>
                        <tr className={`${isClassic ? 'bg-slate-100 border-b-2 border-slate-800' : 'bg-slate-50 border-b border-slate-200'}`}>
                            <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider w-12 text-center border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'}`}>#</th>
                            <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider w-1/3 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'}`}>Description</th>
                            <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider w-24 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'}`}>HSN</th>
                            <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider text-right w-24 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'}`}>Rate</th>
                            <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider text-right w-20 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'}`}>Qty</th>
                            {!isBillOfSupply && (
                                isInterState ? (
                                    <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider text-right w-24 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'}`}>IGST %</th>
                                ) : (
                                    <>
                                        <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider text-right w-24 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'}`}>CGST %</th>
                                        <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider text-right w-24 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'}`}>SGST %</th>
                                    </>
                                )
                            )}
                            <th className={`py-3 px-4 text-xs font-bold ${isClassic ? 'text-slate-900' : 'text-slate-500'} uppercase tracking-wider text-right w-32 ${!isPreview ? 'border-r ' + (isClassic ? 'border-slate-800' : 'border-slate-200') : ''}`}>Amount</th>
                            {!isPreview && <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-12 text-right"></th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                        {products.map((product, index) => {
                            const { totalAmount } = calculateProductTax(product, sellerGST, buyerGST, gstType);

                            return (
                                <tr key={product.id} className={`${(isPreview && !isClassic) ? '' : 'hover:bg-slate-50'} transition-colors ${isClassic ? '' : 'border-b border-slate-200'}`}>
                                    <td className={`px-4 py-2 text-center text-sm ${isClassic ? 'text-slate-900 font-bold' : 'text-slate-500'} border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'} align-top`}>
                                        {index + 1}
                                    </td>
                                    <td className={`px-4 py-2 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'} align-top`}>
                                        {isPreview ? (
                                            <span className={`text-slate-800 font-medium block py-1 ${isClassic ? 'uppercase' : ''}`}>{product.name || '-'}</span>
                                        ) : (
                                            <input
                                                type="text"
                                                value={product.name}
                                                onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                                                className="w-full bg-transparent outline-none text-slate-800 font-medium placeholder:text-slate-300"
                                                placeholder="Item Name"
                                            />
                                        )}
                                    </td>
                                    <td className={`px-4 py-2 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'} align-top`}>
                                        {isPreview ? (
                                            <span className="text-slate-600 text-sm block py-1">{product.hsnCode || '-'}</span>
                                        ) : (
                                            <input
                                                type="text"
                                                value={product.hsnCode}
                                                onChange={(e) => updateProduct(product.id, 'hsnCode', e.target.value)}
                                                className="w-full bg-transparent outline-none text-sm text-slate-600"
                                                placeholder="HSN"
                                            />
                                        )}
                                    </td>
                                    <td className={`px-4 py-2 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'} align-top`}>
                                        {isPreview ? (
                                            <div className="text-right py-1 text-slate-700">{product.rate}</div>
                                        ) : (
                                            <input
                                                type="number"
                                                value={product.rate}
                                                onChange={(e) => updateProduct(product.id, 'rate', e.target.value)}
                                                className="w-full bg-transparent outline-none text-right text-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                placeholder="0.00"
                                                min="0"
                                            />
                                        )}
                                    </td>
                                    <td className={`px-4 py-2 border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'} align-top`}>
                                        {isPreview ? (
                                            <div className="text-right py-1 text-slate-700">{product.quantity}</div>
                                        ) : (
                                            <input
                                                type="number"
                                                value={product.quantity}
                                                onChange={(e) => updateProduct(product.id, 'quantity', e.target.value)}
                                                className="w-full bg-transparent outline-none text-right text-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                min="1"
                                            />
                                        )}
                                    </td>

                                    {!isBillOfSupply && (
                                        isInterState ? (
                                            <td className={`px-4 py-2 text-center border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'} align-top`}>
                                                {isPreview ? (
                                                    <div className="text-center py-1 text-slate-600 text-sm">{product.gstRate}%</div>
                                                ) : (
                                                    <select
                                                        value={product.gstRate}
                                                        onChange={(e) => updateProduct(product.id, 'gstRate', e.target.value)}
                                                        className="w-full bg-transparent outline-none text-center text-sm text-slate-600 cursor-pointer appearance-none py-1 pr-4 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22M6%208l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-no-repeat bg-right"
                                                    >
                                                        <option value="0">0%</option>
                                                        <option value="5">5%</option>
                                                        <option value="12">12%</option>
                                                        <option value="18">18%</option>
                                                        <option value="28">28%</option>
                                                        <option value="40">40%</option>
                                                        <option value="80">80%</option>
                                                    </select>
                                                )}
                                            </td>
                                        ) : (
                                            <>
                                                <td className={`px-2 py-2 text-center border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'} align-top`}>
                                                    {isPreview ? (
                                                        <div className="text-center py-1 text-slate-600 text-sm">{product.gstRate / 2}%</div>
                                                    ) : (
                                                        <select
                                                            value={product.gstRate / 2}
                                                            onChange={(e) => updateProduct(product.id, 'gstRate', parseFloat(e.target.value) * 2)}
                                                            className="w-full bg-transparent outline-none text-center text-sm text-slate-600 cursor-pointer appearance-none py-1 pr-4 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22M6%208l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-no-repeat bg-right"
                                                        >
                                                            <option value="0">0%</option>
                                                            <option value="2.5">2.5%</option>
                                                            <option value="6">6%</option>
                                                            <option value="9">9%</option>
                                                            <option value="14">14%</option>
                                                            <option value="20">20%</option>
                                                            <option value="40">40%</option>
                                                        </select>
                                                    )}
                                                </td>
                                                <td className={`px-2 py-2 text-center border-r ${isClassic ? 'border-slate-800' : 'border-slate-200'} align-top`}>
                                                    {isPreview ? (
                                                        <div className="text-center py-1 text-slate-600 text-sm">{product.gstRate ? product.gstRate / 2 : 0}%</div>
                                                    ) : (
                                                        <select
                                                            value={product.gstRate ? product.gstRate / 2 : 0}
                                                            onChange={(e) => updateProduct(product.id, 'gstRate', parseFloat(e.target.value) * 2)}
                                                            className="w-full bg-transparent outline-none text-center text-sm text-slate-600 cursor-pointer appearance-none py-1 pr-4 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22M6%208l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-no-repeat bg-right"
                                                        >
                                                            <option value="0">0%</option>
                                                            <option value="2.5">2.5%</option>
                                                            <option value="6">6%</option>
                                                            <option value="9">9%</option>
                                                            <option value="14">14%</option>
                                                            <option value="20">20%</option>
                                                            <option value="40">40%</option>
                                                        </select>
                                                    )}
                                                </td>
                                            </>
                                        )
                                    )}

                                    <td className={`px-4 py-2 text-right font-bold text-slate-800 ${isClassic ? 'border-r border-slate-800' : 'border-r border-slate-200'} align-top`}>
                                        <div className="py-1">{formatCurrency(totalAmount)}</div>
                                    </td>
                                    {!isPreview && (
                                        <td className="px-2 py-2 text-center align-top">
                                            <button
                                                onClick={() => removeProduct(product.id)}
                                                className="text-slate-400 hover:text-red-600 transition-colors p-1"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className={`${isClassic ? 'bg-slate-100 border-t-2 border-slate-800 font-bold' : 'bg-slate-50 border-t-2 border-slate-300 font-bold text-slate-800'}`}>
                            <td
                                colSpan={!isBillOfSupply ? (isInterState ? 6 : 7) : 5}
                                className={`px-4 py-3 text-right border-r ${isClassic ? 'border-slate-800' : 'border-slate-300'} uppercase tracking-wider text-xs`}
                            >
                                Total
                            </td>
                            <td className={`px-4 py-3 text-right ${isClassic ? 'border-r border-slate-800' : 'border-r border-slate-300'}`}>
                                {formatCurrency(products.reduce((acc, p) => {
                                    const { totalAmount } = calculateProductTax(p, sellerGST, buyerGST, gstType);
                                    return acc + totalAmount;
                                }, 0))}
                            </td>
                            {!isPreview && <td className="px-2 py-3 text-center border-l-0"></td>}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default ProductTable;

